import { PrinterOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import config from 'config';
import { format } from 'date-fns';
import {
  BloodType,
  ProductListFilterInput,
  ProductListSort,
  ProductOutput,
  ProductStatus,
} from 'gql/types';
import { ISortOption } from 'modules/common/interfaces';
import { createSortOrderFactory } from 'modules/common/utils/sort/createSortOrderFactory';
import AvailabilityCheckbox from 'modules/products/components/AvailabilityCheckbox';
import ProductDropdownActions from 'modules/products/components/ProductDropdownActions';
import { getCryoType } from 'modules/products/utils/getCryoType';
import { getTranslation } from 'utils/getTranslation';

import styles from './styles.module.scss';

import { CryoTag } from '../CryoTag';

const t = getTranslation('products.ProductsTable');

const normalizeFilterValue = (value: string | string[] | null | undefined) => {
  if (typeof value === 'string') {
    return [value];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return null;
};

type ParamTypes = {
  onAvailable?: (product: ProductOutput) => void;
  onPrint?: (product: ProductOutput) => void;
  productCategories?: Record<number, string>;
  canCreateLabels: boolean;
  canMakeProductsAvailable: boolean;
  filterValue?: ProductListFilterInput;
  showSorterTooltip?: boolean;
  sort?: ISortOption<ProductListSort>;
  regionFilterOptions: { text: string; value: string }[] | undefined;
};

export const getColumns = (params: ParamTypes): ColumnsType<ProductOutput> => {
  const getDefaultSort = createSortOrderFactory<ProductListSort>(params.sort);
  const {
    onAvailable,
    onPrint,
    productCategories,
    canCreateLabels,
    canMakeProductsAvailable,
    filterValue,
    showSorterTooltip,
    regionFilterOptions,
  } = params;

  const productsFilters =
    productCategories &&
    Object.keys(productCategories).map((categoryId) => {
      return {
        text: productCategories[Number(categoryId)],
        value: Number(categoryId),
      };
    });

  return [
    ...(canCreateLabels
      ? [
          {
            title: t('label'),
            align: 'center' as const,
            render: (product: ProductOutput) => {
              if (product.status === ProductStatus.Discarded) {
                return null;
              }
              return (
                <PrinterOutlined
                  className={styles.printIcon}
                  onClick={() => onPrint && onPrint(product)}
                />
              );
            },
          },
        ]
      : []),
    {
      title: t('donationId'),
      render: ({ donation }: ProductOutput) => donation.shortId,
    },
    {
      title: t('productId'),
      render: (record: ProductOutput) => record.shortId,
    },
    {
      title: t('unitId'),
      render: (product: ProductOutput) => {
        if (
          !product.bloodIdentifiers ||
          product.bloodIdentifiers.length === 0
        ) {
          return '-';
        }

        return product.bloodIdentifiers[0].unitId;
      },
    },
    {
      title: t('orderId'),
      key: 'orderId',
      render: ({ woocommerceOrderId }: ProductOutput) => {
        return woocommerceOrderId || '-';
      },
    },
    {
      title: t('petName'),
      sorter: true,
      sortOrder: getDefaultSort(ProductListSort.DonorName),
      showSorterTooltip,
      key: ProductListSort.DonorName,
      render: ({ donation }: ProductOutput) => donation?.donor?.name,
    },
    {
      title: t('ownerLastName'),
      sorter: true,
      sortOrder: getDefaultSort(ProductListSort.OwnerLastName),
      showSorterTooltip,
      key: ProductListSort.OwnerLastName,
      render: ({ donation }: ProductOutput) => donation?.donor?.user?.lastName,
    },
    {
      title: t('region'),
      key: 'regionName',
      render: ({ regionPreference }: ProductOutput) => regionPreference?.name,
      filters: regionFilterOptions,
      filteredValue: normalizeFilterValue(filterValue?.regionName),
    },
    {
      title: t('mls'),
      sorter: true,
      sortOrder: getDefaultSort(ProductListSort.Value),
      showSorterTooltip,
      key: ProductListSort.Value,
      render: ({ value }: ProductOutput) => value,
    },
    {
      title: t('expirationDate'),
      key: ProductListSort.ExpirationDate,
      render: ({ expirationDate }: ProductOutput) => {
        return format(new Date(expirationDate), config.DATE_FORMATS.DATE);
      },
      sorter: true,
      sortOrder: getDefaultSort(ProductListSort.ExpirationDate),
      showSorterTooltip,
    },
    {
      title: t('product'),
      render: (product: ProductOutput) => {
        const cryoType = getCryoType(product.woocommerceProductId);
        const productName = productCategories?.[product.woocommerceCategoryId];

        return (
          <Space size={4} className={styles.productNameWrapper}>
            <span>{productName}</span>

            {cryoType && <CryoTag type={cryoType} className={styles.cryoTag} />}
          </Space>
        );
      },
      key: 'woocommerceCategory',
      filters: productsFilters,
      filteredValue: filterValue?.woocommerceCategory || null,
    },
    {
      title: t('bloodType'),
      render: ({ donation }: ProductOutput) =>
        donation?.donor?.bloodType
          ? t(`${donation?.donor?.bloodType?.toLowerCase()}`)
          : '',
      key: 'bloodType',
      filters: Object.values(BloodType).map((bloodType) => {
        return {
          text: t(bloodType.toLowerCase()),
          value: bloodType,
        };
      }),
      filteredValue: filterValue?.bloodType || null,
    },
    ...(canMakeProductsAvailable
      ? [
          {
            title: t('available'),
            align: 'center' as const,
            render: (product: ProductOutput) => {
              if (!product.publishable) {
                return null;
              }
              return (
                <AvailabilityCheckbox
                  product={product}
                  onChecked={() => onAvailable && onAvailable(product)}
                />
              );
            },
          },
        ]
      : []),
    {
      title: t('soldInfo'),
      render: (product: ProductOutput) => product.status,
      key: 'statuses',
      filters: Object.values(ProductStatus).map((status) => {
        return {
          text: t(status.toLowerCase()),
          value: status,
        };
      }),
      filteredValue: filterValue?.statuses || null,
    },

    {
      render: (row: ProductOutput) => <ProductDropdownActions product={row} />,
    },
  ];
};
